import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { catchError, filter, map, Observable, of, Subject, switchMap, throwError } from 'rxjs';
import { TokenService } from '../../services/auth/token.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  constructor(private tokenService: TokenService) {

  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const sessionFromStore = JSON.parse(sessionStorage.getItem('session')!);
    return of(sessionFromStore).pipe(
      map(session => this.addToken(request, session)),
      switchMap(req => next.handle(req)),
      // refresh token if response code is 401
      catchError(error => {
        const err = { ...error };
        let formattedErr = null;
        if (typeof err.error === 'string') {
          formattedErr = JSON.parse(err.error);
        } else {
          formattedErr = err.error;
        }
        if (error.status === 401) {
          return this.isTokenExpired(formattedErr)
            .pipe(
              switchMap(isExpired => {
                return isExpired
                  ? this.handle401Error(request, next, sessionFromStore)
                  : throwError(() => error);
              })
            );
        } else {
          return throwError(() => error);
        }
      })
    );
  }


  private addToken(request: HttpRequest<any>, session: any): HttpRequest<any> {
    const url = request.url;
    const urlComponents = url.split('/');
    const uriPart = urlComponents[urlComponents.length - 1] ? urlComponents[urlComponents.length - 1] : '';
    const isStaticAsset = urlComponents[1] === 'assets';
    const isGCPStorageUrl = urlComponents.find(comp => comp === "storage.googleapis.com");
    // TODO: the check for uri should not be here
    // remove after permission is added to server
    if (session && session.accessToken && uriPart !== 'token' && !isStaticAsset && !isGCPStorageUrl) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${session.accessToken}`
        }
      });
    } else {
      return request;
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, sessionFromStore: any) {

    // refresh the token. if there are multiple requests with invalid access tokens
    // then a refresh will be requested for each of them. this will mean that some
    // will be used only for one request. This approach seems less error prone than
    // creating a queue of requests waiting for refreshed token
    return of(sessionFromStore).pipe(
      filter(session => session != null),
      switchMap(session => {
        return new Promise((resolve, reject) => {
          this.tokenService.refreshToken(session.refreshToken).then(
            accessToken => {
              resolve(accessToken);
            }
          );
        }).catch(error => {
          throwError(error);
        });
      }),
      switchMap((accessToken: any) => {
        return next.handle(this.addToken(request, { accessToken }));
      })
    );
  }

  private isTokenExpired(error: any): Observable<boolean> {
    if (error) {
      return of(error)
        .pipe(
          switchMap(error => {
            return (error instanceof Blob)
              ? this.readErrorFromBlob(error)
              : this.readErrorDetailFromJson(error)
          }),
          map(errorDetails =>
            errorDetails
            && errorDetails
              .find((errorDetail: any) => errorDetail.errorCode
                === ERROR_TOKEN_EXPIRED))
        );
    }
    return of(false);
  }

  readErrorDetailFromJson(json: any): Observable<any[]> {
    const errorDetails = json.errorDetails as any[];
    return of(errorDetails);
  }

  readErrorFromBlob(error: any): Observable<any> {

    const result = new Subject<any>();
    const reader = new FileReader();
    reader.onload = (e: Event) => {
      try {
        const error = JSON.parse((<any>e.target).result);
        result.next(error.errorDetails);
        result.complete();
      } catch (e) {
        result.error(e);
        result.complete();
      }
    };
    reader.onerror = (e) => {
      result.error(e);
      result.complete();
    };
    reader.readAsText(error);
    return result;

  }
}

// error code for token expiry
const ERROR_TOKEN_EXPIRED = 'tcat.security.tokenExpired';

